// Lightweight accessible carousel with a seamless loop (presentation only — no learning state).
//
//  • Cards per view come from the CSS custom property --per-view (3 desktop, 2 tablet,
//    1 phone). The carousel moves one GROUP (= cards per view) at a time; dots and the
//    counter are per group.
//  • Seamless loop: the first cards are cloned (inert, aria-hidden, no test ids) after the
//    last card. Going past the last group slides on into the clones — which look exactly
//    like the first group — and then snaps, without a transition, to the real first group.
//    Previous from the first group does the reverse. No rewind, no blank space.
//  • Only the track moves (transform). All cards are rendered once and stretch to the
//    tallest, so the section height never changes.
//  • Autoplay (≥ 6 s) runs only while the carousel is on screen and the tab is visible.
//    It pauses while hovered or focused and resumes afterwards; after a manual move it
//    waits HOLD_MS so the visitor can read; the Pause button stops it until Play.
//    Never with prefers-reduced-motion (the CSS also removes the transition).
//  • Off-screen cards are aria-hidden + inert. Timers, observers and listeners are removed
//    by el.__cleanup() (called by the router before the page is replaced).
import { h } from './h.js';
import { icon } from './icons.js';

export const HOLD_MS = 12000;
export const prefersReducedMotion = () => {
  try { return !!globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};
const MAX_CLONES = 3;

/**
 * @param {{ id: string, label: string, slides: Node[], interval?: number, autoplay?: boolean,
 *           slideLabel?: (i: number, n: number) => string, onChange?: (first: number, perView: number) => void }} o
 */
export function carousel({ id, label, slides, interval = 7000, autoplay = true, slideLabel, onChange, sideArrows = false }) {
  const n = slides.length;
  const reduce = prefersReducedMotion();
  let index = 0;               // first REAL card shown (multiple of perView, or 0)
  let offset = 0;              // card offset the track is translated to (may point into the clones)
  let userPaused = reduce || !autoplay;
  let hover = false; let focus = false; let inView = false; let holdUntil = 0; let timer = null; let snapTimer = null; let dead = false;

  const items = slides.map((s, i) => h('li', { class: 'rz-car-slide', role: 'group', 'aria-roledescription': 'slide', 'aria-label': slideLabel ? slideLabel(i, n) : `${i + 1} of ${n}`, 'data-testid': `${id}-slide-${i}` }, s));
  const clones = items.slice(0, Math.min(MAX_CLONES, n)).map((li) => {
    const c = li.cloneNode(true);
    c.removeAttribute('data-testid'); c.removeAttribute('role'); c.removeAttribute('aria-roledescription'); c.removeAttribute('aria-label');
    for (const x of [c, ...c.querySelectorAll('[data-testid],[id]')]) { x.removeAttribute('data-testid'); x.removeAttribute('id'); }
    c.classList.add('rz-car-clone'); c.setAttribute('aria-hidden', 'true'); c.setAttribute('inert', '');
    return c;
  });
  const track = h('ul', { class: 'rz-car-track' }, items, clones);
  const viewport = h('div', { class: 'rz-car-viewport', tabindex: '0', 'aria-label': `${label} — use the left and right arrow keys to move` }, track);
  const status = h('span', { class: 'sr-only', 'aria-live': 'polite', 'data-testid': `${id}-status` });
  const counter = h('span', { class: 'rz-car-count', 'aria-hidden': 'true', 'data-testid': `${id}-count` });
  const dots = h('div', { class: 'rz-car-dots', role: 'group', 'aria-label': `${label}: choose a group of cards` });
  const prev = h('button', { class: 'rz-car-btn', type: 'button', 'aria-label': `Previous — ${label}`, 'data-testid': `${id}-prev`, onclick: () => manual(() => step(-1)) }, icon('arrowLeft', { size: 18 }));
  const next = h('button', { class: 'rz-car-btn', type: 'button', 'aria-label': `Next — ${label}`, 'data-testid': `${id}-next`, onclick: () => manual(() => step(1)) }, icon('arrowRight', { size: 18 }));
  const play = h('button', { class: 'rz-car-play', type: 'button', 'data-testid': `${id}-play`, hidden: !autoplay || reduce ? true : null, onclick: () => { userPaused = !userPaused; holdUntil = 0; schedule(); } });
  // optional large arrows on the card edges (pointer convenience; the labelled controls stay the keyboard path)
  const side = sideArrows ? [
    h('button', { class: 'rz-car-side prev', type: 'button', tabindex: '-1', 'aria-hidden': 'true', 'data-testid': `${id}-side-prev`, onclick: () => manual(() => step(-1)) }, icon('arrowLeft', { size: 20 })),
    h('button', { class: 'rz-car-side next', type: 'button', tabindex: '-1', 'aria-hidden': 'true', 'data-testid': `${id}-side-next`, onclick: () => manual(() => step(1)) }, icon('arrowRight', { size: 20 })),
  ] : [];
  const controls = h('div', { class: 'rz-car-controls' }, prev, dots, next, counter, play);
  const root = h('div', { class: `rz-carousel ${sideArrows ? 'has-side' : ''}`, role: 'region', 'aria-roledescription': 'carousel', 'aria-label': label, 'data-testid': id, 'data-cleanup': '' },
    h('div', { class: 'rz-car-stage' }, viewport, side),
    controls,
    status);

  const perView = () => Math.max(1, Math.min(n, parseInt(getComputedStyle(root).getPropertyValue('--per-view'), 10) || 1));
  const groups = () => Math.ceil(n / perView());
  const lastStart = () => (groups() - 1) * perView();
  const all = () => [...items, ...clones];
  const place = (instant = false) => {
    const target = all()[offset];
    if (instant) track.classList.add('no-anim');
    track.style.transform = `translateX(${-(target?.offsetLeft || 0)}px)`;
    if (instant) { void track.offsetWidth; track.classList.remove('no-anim'); }
  };

  let builtGroups = -1;
  function buildDots() {
    const g = groups(); const pv = perView();
    if (g === builtGroups && dots.dataset.pv === String(pv)) return;
    builtGroups = g; dots.dataset.pv = String(pv);
    dots.textContent = '';
    for (let i = 0; i < g; i++) {
      const a = i * pv + 1; const b = Math.min(n, a + pv - 1);
      dots.append(h('button', { class: 'rz-car-dot', type: 'button', 'data-testid': `${id}-dot-${i}`, 'aria-label': a === b ? `Show card ${a}` : `Show cards ${a} to ${b}`, onclick: () => manual(() => goGroup(i)) }));
    }
  }
  /** Which real cards are visible for the current index (wraps into the first cards on a short last group). */
  const shown = () => { const pv = perView(); return [...Array(pv)].map((_, k) => (index + k) % n); };
  function sync() {
    const pv = perView(); const vis = new Set(shown());
    items.forEach((el, i) => {
      const on = vis.has(i);
      el.classList.toggle('is-visible', on);
      if (on) { el.removeAttribute('aria-hidden'); el.removeAttribute('inert'); } else { el.setAttribute('aria-hidden', 'true'); el.setAttribute('inert', ''); }
    });
    const g = Math.floor(index / pv);
    [...dots.children].forEach((d, i) => { d.setAttribute('aria-current', i === g ? 'true' : 'false'); d.classList.toggle('active', i === g); });
    const nums = shown().map((i) => i + 1);
    const range = nums.length === 1 ? `${nums[0]}` : nums.every((v, k) => k === 0 || v === nums[k - 1] + 1) ? `${nums[0]}–${nums.at(-1)}` : nums.join(', ');
    counter.textContent = `${range} of ${n}`;
    status.textContent = `Showing ${nums.length > 1 ? 'cards' : 'card'} ${range} of ${n}`;
    const playing = !userPaused;
    play.textContent = playing ? '❚❚ Pause' : '▶ Play';
    play.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} automatic movement — ${label}`);
    play.setAttribute('aria-pressed', playing ? 'false' : 'true');
    root.dataset.index = String(index);
    root.dataset.group = String(g);
    root.dataset.playing = playing ? 'true' : 'false';
    onChange?.(index, pv);
  }
  function settle() {
    // after sliding into the clones, jump (invisibly) back to the identical real cards
    clearTimeout(snapTimer); snapTimer = null;
    if (offset !== index) { offset = index; place(true); }
  }
  function moveTo(newIndex, viaOffset) {
    settle();
    index = newIndex; offset = viaOffset ?? newIndex;
    place(reduce);
    sync();
    if (offset !== index) {
      if (reduce) settle();
      else snapTimer = setTimeout(settle, 700); // transition is .55 s
    }
  }
  function step(dir) {
    const pv = perView();
    if (dir > 0) {
      const nx = index + pv;
      if (nx < n) moveTo(nx);
      else moveTo(0, n); // slide on into the clones of the first cards, then snap to 0
    } else if (index > 0) moveTo(Math.max(0, index - pv));
    else {
      // from the first group: jump (invisibly) to the identical clones, then slide back to the last group
      settle(); offset = n; place(true);
      index = lastStart(); offset = index; place(reduce); sync();
    }
  }
  function goGroup(i) { moveTo(Math.min(i * perView(), lastStart())); }
  // a manual move takes precedence: autoplay waits HOLD_MS before moving again
  function manual(fn) { holdUntil = Date.now() + HOLD_MS; fn(); schedule(); }

  function stop() { clearTimeout(timer); timer = null; }
  function schedule() {
    stop();
    if (dead) return;
    sync();
    const blocked = [userPaused && 'paused', hover && 'hover', focus && 'focus', !inView && 'offscreen', document.hidden && 'hidden'].filter(Boolean);
    root.dataset.blocked = blocked.join(' ');
    if (blocked.length) { root.dataset.running = 'false'; return; }
    root.dataset.running = 'true';
    const wait = Math.max(Math.max(6000, interval), holdUntil - Date.now());
    timer = setTimeout(() => { if (!dead) { holdUntil = 0; step(1); schedule(); } }, wait);
  }
  function onResize() {
    if (dead) return;
    buildDots();
    const pv = perView();
    settle();
    index = Math.min(Math.floor(index / pv) * pv, lastStart()); offset = index;
    place(true); sync();
  }
  const onVis = () => schedule();
  root.addEventListener('mouseenter', () => { hover = true; schedule(); });
  root.addEventListener('mouseleave', () => { hover = false; schedule(); });
  // keyboard focus pauses; a mouse click on a control does not (it is a manual move instead)
  root.addEventListener('focusin', (e) => { focus = e.target.matches(':focus-visible'); schedule(); });
  root.addEventListener('focusout', (e) => { if (!root.contains(e.relatedTarget)) { focus = false; schedule(); } });
  viewport.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); manual(() => step(1)); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); manual(() => step(-1)); }
  });
  track.addEventListener('transitionend', (e) => { if (e.target === track && e.propertyName === 'transform') settle(); });
  // swipe (touch / pen / mouse drag); vertical scrolling stays native (touch-action: pan-y)
  let sx = null; let sy = 0;
  viewport.addEventListener('pointerdown', (e) => { sx = e.clientX; sy = e.clientY; });
  viewport.addEventListener('pointerup', (e) => {
    if (sx == null) return;
    const dx = e.clientX - sx; const dy = e.clientY - sy; sx = null;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) manual(() => step(dx < 0 ? 1 : -1));
  });
  viewport.addEventListener('pointercancel', () => { sx = null; });
  addEventListener('resize', onResize);
  document.addEventListener('visibilitychange', onVis);
  const io = typeof IntersectionObserver === 'function'
    ? new IntersectionObserver((es) => { inView = es.some((x) => x.isIntersecting); schedule(); }, { threshold: 0.35 })
    : null;
  if (io) io.observe(root); else inView = true;
  root.__cleanup = () => {
    dead = true; stop(); clearTimeout(snapTimer); io?.disconnect(); root.dataset.running = 'false'; root.dataset.cleaned = 'true';
    removeEventListener('resize', onResize); document.removeEventListener('visibilitychange', onVis);
  };
  // first layout once mounted (dots depend on the CSS --per-view)
  requestAnimationFrame(() => { if (root.isConnected) { buildDots(); place(true); sync(); schedule(); } });
  buildDots(); sync();

  return { el: root, controls, go: (i) => manual(() => moveTo(Math.min(Math.floor(i / perView()) * perView(), lastStart()))), get index() { return index; }, perView };
}
